import React from "react";
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Grid, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Alert,
  Chip,
  Divider,
  useTheme
} from "@mui/material";
import {
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Public as PublicIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';

const About = () => {
  const theme = useTheme();

  const misconductExamples = [
    'Concealing exculpatory evidence',
    'Ignoring information',
    'Deception at trial',
    'Perjury by police officers',
    'Misstatements by prosecutors',
    'Evidence tampering',
    'Fabrication of evidence',
    'Forensic fraud',
    'Fake crimes',
    'Fictitious confessions',
    'Improper interrogations',
    'Coercion of witnesses',
    'Coercion of defendants',
    'Accepting bribes',
    'Racial profiling',
    'Conflict of interest',
    'Violation of procedural rules',
    'Violation of Rules of Professional Conduct',
    'Inappropriate remarks/courtroom behavior'
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          About The Reckoning Project
        </Typography>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 600, mb: 3 }}>
          Promoting transparency and justice for those harmed by misconduct in the U.S. criminal legal system.
        </Typography>
      </Box>

      {/* Mission Statement */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Our Mission
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            The Reckoning Project works to bring justice to people who have been negatively affected by 
            official misconduct in the criminal legal system. We are not a legal services organization 
            and we do not represent people ourselves or refer them to attorneys. Instead, we are gathering 
            data for a large-scale research project tracking instances of official misconduct to create 
            the most comprehensive database of official legal misconduct that has existed to date.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            We hope our database will create transparency and accountability for legal actors, as well as an incentive for positive law enforcement practices.
          </Typography>
        </CardContent>
      </Card>

      {/* Definition of Misconduct */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Definition of Misconduct
          </Typography>
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body1">
              <strong>"Misconduct"</strong> refers to situations where an official actor abused their authority 
              in a specific case and, in the process, violated people's constitutional rights, state statutes, 
              and/or rules of professional conduct. In most cases, the terms "misconduct" and "illegal" will overlap.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            The Reckoning Project is specifically focused on collecting information about cases where individual 
            actors (such as police officers, prosecutors, judges, expert witnesses, etc.) abused their power and 
            committed illegal and/or unethical actions that resulted in cases of actual innocence, or people being 
            overcharged, excessively sentenced, or otherwise unjustly affected by the misconduct.
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            We are not including reports of misconduct by correctional officers or any prison staff in this project. 
            We typically do not track misconduct by defense attorneys, unless they act with extreme maliciousness 
            or break the law while working in their capacity as defense attorneys.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
            Examples of Misconduct include:
          </Typography>
          <Grid container spacing={1}>
            {misconductExamples.map((example, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Chip
                  label={example}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    m: 0.5,
                    width: '100%',
                    justifyContent: 'flex-start',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      textAlign: 'left'
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Our Approach - Three Pillars */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
        Our Approach
      </Typography>

      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <CardContent sx={{ p: 4, flexGrow: 1 }}>
              <PublicIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Mapping Misconduct Across the System
              </Typography>
              <Typography color="text.secondary">
                We track and code all proven incidents of misconduct by officials, using data from 
                official oversight bodies, established databases and media archives and more.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <CardContent sx={{ p: 4, flexGrow: 1 }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Uncovering Systemic Misconduct
              </Typography>
              <Typography color="text.secondary">
                We gather and work to verify reports from incarcerated individuals alleging misconduct, 
                transforming isolated stories into comprehensive analysis.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <CardContent sx={{ p: 4, flexGrow: 1 }}>
              <AnalyticsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Exposing Patterns: Tracking Repeat Offenders
              </Typography>
              <Typography color="text.secondary">
                We maintain a comprehensive database to estimate misconduct's true extent and identify 
                involved actors, connecting dots to reveal patterns.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Geographic Scope */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Geographic Scope
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            Piloted in Pennsylvania, The Reckoning Project is expanding requests for information about 
            misconduct to other states, and we will gladly accept case information from any geographic 
            location as we gradually expand across the United States.
          </Typography>
        </CardContent>
      </Card>

      {/* How We Work */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            How We Work
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <VerifiedIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Data Collection"
                secondary="We gather information from public records, watchdog sites, legal databases, media outlets, and direct reports from those affected."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Verification Process"
                secondary="Reports are only made publicly available if we find external sources of corroboration (legal filings, newspaper articles, etc.)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AnalyticsIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Pattern Analysis"
                secondary="We apply crowd-sourcing technology and data science to identify patterns and move from isolated accounts to systematic analysis."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Promoting Justice"
                secondary="Our goal is to help those affected achieve greater justice while incentivizing appropriate behavior among legal actors."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Important Disclaimer */}
      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Important Disclaimer
        </Typography>
        <Typography variant="body1">
          Please note that we are not a legal services organization and we do not represent people 
          ourselves or refer them to attorneys. Instead, we gather data about official misconduct 
          in the hopes that our larger database will help to identify previously unknown patterns 
          of abuse that will eventually allow some people affected by misconduct to achieve greater justice. 
          Although we recognize that many incarcerated people may have received excessive sentences 
          or been negatively affected by past and current laws, The Reckoning Project can only track 
          cases that involve official misconduct by specific legal actors.
        </Typography>
      </Alert>

      {/* Contact Information */}
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Get Involved
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            If you or someone you know has experienced misconduct, please request a questionnaire through our questionnaire. 
            For general inquiries about The Reckoning Project, contact us at{' '}
            <a href="mailto:info@thereckoningproject.org" style={{ color: theme.palette.primary.main }}>
              info@thereckoningproject.org
            </a>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default About;
